import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  PlayArrow,
  Stop,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Info,
  ExpandMore,
  Download,
  Delete,
  History,
  Assessment,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { bulkImportAPI, accountsAPI } from '../../../services/leadGenAPI';
import CampaignSelector from '../../../components/CampaignSelector';

const steps = [
  'Select File & Type',
  'Preview Data',
  'Configure Import',
  'Upload & Process',
];

const importTypes = [
  { value: 'suppression_zipcodes', label: 'Zip Codes', description: 'Geographic suppression by postal codes (blacklist/whitelist)' },
  { value: 'suppression_states', label: 'States', description: 'Geographic suppression by state/province (blacklist/whitelist)' },
  { value: 'suppression_cities', label: 'Cities', description: 'Geographic suppression by city (blacklist/whitelist)' },
  { value: 'suppression_email_domains', label: 'Email Domains', description: 'Contact suppression by email domain (blacklist/whitelist)' },
  { value: 'suppression_phone_codes', label: 'Phone Area Codes', description: 'Contact suppression by phone area code (blacklist/whitelist)' },
];

const templateExamples = {
  suppression_zipcodes: [
    { zipcode: '12345', operator: 'in', description: 'High-value zip codes' },
    { zipcode: '90210', operator: 'equals', description: 'Beverly Hills' },
    { zipcode: '67890', operator: 'starts_with', description: 'West Coast area' },
  ],
  suppression_states: [
    { state: 'California', operator: 'equals', description: 'Single state' },
    { state: 'New York', operator: 'in', description: 'Multiple states' },
    { state: 'Texas', operator: 'starts_with', description: 'States starting with Texas' },
  ],
  suppression_cities: [
    { city: 'New York', state: 'NY', operator: 'equals', description: 'East Coast City' },
    { city: 'Los Angeles', state: 'CA', operator: 'in', description: 'West Coast City' },
    { city: 'Chicago', state: 'IL', operator: 'starts_with', description: 'Midwest City' },
  ],
  suppression_email_domains: [
    { email_domain: 'example.com', operator: 'equals', description: 'Test domain' },
    { email_domain: 'spam.com', operator: 'ends_with', description: 'Spam domain' },
    { email_domain: 'blocked.org', operator: 'in', description: 'Blocked domain' },
  ],
  suppression_phone_codes: [
    { phone_code: '212', operator: 'equals', description: 'New York area code' },
    { phone_code: '310', operator: 'in', description: 'Los Angeles area code' },
    { phone_code: '312', operator: 'starts_with', description: 'Chicago area code' },
  ],
};

const operatorOptions = [
  'equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with',
  'in', 'not_in', 'greater_than', 'less_than', 'between', 'is_empty', 'is_not_empty'
];

const fileTypes = [
  { value: 'csv', label: 'CSV', extensions: ['.csv'] },
  { value: 'excel', label: 'Excel', extensions: ['.xlsx', '.xls'] },
  { value: 'json', label: 'JSON', extensions: ['.json'] },
  { value: 'txt', label: 'Text', extensions: ['.txt'] },
];

const BulkUploadSuppression = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importType, setImportType] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accountId, setAccountId] = useState(1); // Default account ID
  const [previewData, setPreviewData] = useState(null);
  const [uploadOptions, setUploadOptions] = useState({
    skipDuplicates: true,
    validateData: true,
    batchSize: 1000,
    targetCampaignIds: [],
    autoAssignCampaigns: true,
    campaignSpecificImport: false,
    description: '',
    list_type: 'blacklist', // 'blacklist' or 'whitelist'
    default_operator: 'in', // Default operator for bulk imports
  });
  const [currentJob, setCurrentJob] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [showJobsDialog, setShowJobsDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);

  // Load existing jobs on component mount
  useEffect(() => {
    loadJobs();
  }, []);

  // Fetch accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Update accountId when selectedAccount changes
  useEffect(() => {
    if (selectedAccount) {
      setAccountId(selectedAccount);
    }
  }, [selectedAccount]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const loadJobs = async () => {
    try {
      const response = await bulkImportAPI.getImportJobs({ account_id: accountId });
      setJobs(response.results || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await accountsAPI.getAccounts();
      setAccounts(response);
      // Set the first account as default if available
      if (response && response.length > 0) {
        setSelectedAccount(response[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setError('Failed to load accounts');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
      setSuccess('');
    }
  };

  const handleImportTypeChange = (event) => {
    setImportType(event.target.value);
    setError('');
    setSuccess('');
  };

  const handleAccountChange = (event) => {
    setSelectedAccount(event.target.value);
    setError('');
    setSuccess('');
  };

  const downloadTemplate = (importType) => {
    const examples = templateExamples[importType];
    if (!examples) return;
    // Always include operator column
    const columns = Object.keys(examples[0]);
    if (!columns.includes('operator')) columns.splice(1, 0, 'operator');
    const csvContent = [
      columns.join(','),
      ...examples.map(row => columns.map(col => row[col] || '').join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!selectedAccount) {
        setError('Please select an account');
        return;
      }
      if (!selectedFile || !importType) {
        setError('Please select a file and import type');
        return;
      }
      await handlePreview();
    } else if (activeStep === 1) {
      if (!previewData) {
        setError('Please preview the file first');
        return;
      }
    } else if (activeStep === 2) {
      await handleUpload();
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePreview = async () => {
    if (!selectedFile || !importType) return;

    setLoading(true);
    setError('');
    try {
      const response = await bulkImportAPI.previewFile(selectedFile, importType, 10);
      setPreviewData(response);
      setSuccess('File preview generated successfully');
    } catch (error) {
      setError(`Preview failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !importType) return;

    // Validate campaign-specific import
    if (uploadOptions.campaignSpecificImport && uploadOptions.targetCampaignIds.length === 0) {
      setError('Campaign-specific import requires at least one target campaign to be selected');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const uploadData = {
        ...uploadOptions,
        list_type: uploadOptions.list_type, // Include list type in the upload
        default_operator: uploadOptions.default_operator || 'in',
      };
      const response = await bulkImportAPI.uploadFile(selectedFile, importType, accountId, uploadData);
      setCurrentJob(response);
      
      let successMessage = `File uploaded successfully. Job ID: ${response.job_id}`;
      if (response.target_campaigns && response.target_campaigns.length > 0) {
        successMessage += `\nAssigned to ${response.target_campaigns.length} campaign(s)`;
      }
      setSuccess(successMessage);
      
      // Start processing automatically
      await bulkImportAPI.startJobProcessing(response.job_id);
      startProgressMonitoring(response.job_id);
    } catch (error) {
      setError(`Upload failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const startProgressMonitoring = (jobId) => {
    progressIntervalRef.current = setInterval(async () => {
      try {
        const job = await bulkImportAPI.getImportJob(jobId);
        setJobProgress(job.progress_percentage || 0);
        setJobStatus(job.status);
        
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(progressIntervalRef.current);
          setCurrentJob(job);
          loadJobs(); // Refresh jobs list
        }
      } catch (error) {
        console.error('Failed to get job progress:', error);
      }
    }, 2000);
  };

  const handleJobAction = async (jobId, action) => {
    try {
      switch (action) {
        case 'start':
          await bulkImportAPI.startJobProcessing(jobId);
          break;
        case 'cancel':
          await bulkImportAPI.cancelJob(jobId);
          break;
        case 'retry':
          await bulkImportAPI.retryJob(jobId);
          break;
      }
      loadJobs();
    } catch (error) {
      setError(`Job action failed: ${error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'processing': return <CircularProgress size={20} />;
      case 'pending': return <Info color="info" />;
      default: return <Info />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Select File and Import Type
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Account Selection
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select Account</InputLabel>
                      <Select
                        value={selectedAccount}
                        onChange={handleAccountChange}
                        label="Select Account"
                      >
                        {accounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      File Selection
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls,.json,.txt"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => fileInputRef.current?.click()}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose File
                    </Button>
                    {selectedFile && (
                      <Box>
                        <Typography variant="body2" color="text.primary">
                          <strong>File:</strong> {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Size:</strong> {formatFileSize(selectedFile.size)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Type:</strong> {selectedFile.type || 'Unknown'}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Import Type
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Select Import Type</InputLabel>
                      <Select
                        value={importType}
                        onChange={handleImportTypeChange}
                        label="Select Import Type"
                      >
                        {importTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box>
                              <Typography variant="body1" color="text.primary">
                                {type.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {type.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {importType && (
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => downloadTemplate(importType)}
                        fullWidth
                      >
                        Download Template
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Preview Data
            </Typography>
            {previewData ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Chip label={`Total Rows: ${previewData.total_rows}`} color="primary" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Chip label={`Preview Rows: ${previewData.preview_rows}`} color="info" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Chip label={`Format: ${previewData.file_format?.toUpperCase()}`} color="secondary" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Chip label={`Columns: ${previewData.columns?.length || 0}`} color="default" />
                  </Grid>
                </Grid>
                {/* Show warning if operator column is missing */}
                {previewData.columns && !previewData.columns.includes('operator') && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Note:</strong> No <code>operator</code> column detected. The default operator (<code>{uploadOptions.default_operator}</code>) will be used for all rows.
                  </Alert>
                )}
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {previewData.columns?.map((column, index) => (
                          <TableCell key={index} color="text.primary">
                            <strong>{column}</strong>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.preview_data?.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {previewData.columns?.map((column, colIndex) => (
                            <TableCell key={colIndex} color="text.primary">
                              {row[column] || ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Click "Preview" to see the data
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Configure Import Options
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Operator Usage:</strong> You can specify an <code>operator</code> column in your CSV to control how each rule is applied.<br />
              Supported operators: <code>equals</code>, <code>not_equals</code>, <code>contains</code>, <code>not_contains</code>, <code>starts_with</code>, <code>ends_with</code>, <code>in</code>, <code>not_in</code>, <code>greater_than</code>, <code>less_than</code>, <code>between</code>, <code>is_empty</code>, <code>is_not_empty</code>.<br />
              <br />
              <strong>Example:</strong>
              <pre style={{ margin: 0 }}>
zipcode,operator,description
12345,in,High-value zip codes
90210,equals,Beverly Hills
67890,starts_with,West Coast area
              </pre>
            </Alert>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Default Operator</InputLabel>
              <Select
                value={uploadOptions.default_operator}
                onChange={e => setUploadOptions(prev => ({ ...prev, default_operator: e.target.value }))}
                label="Default Operator"
              >
                {operatorOptions.map(op => (
                  <MenuItem key={op} value={op}>{op}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Processing Options
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Batch Size</InputLabel>
                      <Select
                        value={uploadOptions.batchSize}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, batchSize: e.target.value }))}
                        label="Batch Size"
                      >
                        <MenuItem value={500}>500 records</MenuItem>
                        <MenuItem value={1000}>1,000 records</MenuItem>
                        <MenuItem value={2000}>2,000 records</MenuItem>
                        <MenuItem value={5000}>5,000 records</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Skip Duplicates</InputLabel>
                      <Select
                        value={uploadOptions.skipDuplicates}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, skipDuplicates: e.target.value }))}
                        label="Skip Duplicates"
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                      <InputLabel>Validate Data</InputLabel>
                      <Select
                        value={uploadOptions.validateData}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, validateData: e.target.value }))}
                        label="Validate Data"
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      List Configuration
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>List Type</InputLabel>
                      <Select
                        value={uploadOptions.list_type}
                        onChange={(e) => setUploadOptions(prev => ({ ...prev, list_type: e.target.value }))}
                        label="List Type"
                      >
                        <MenuItem value="blacklist">
                          <Box display="flex" alignItems="center" gap={1}>
                            <BlockIcon color="error" />
                            Blacklist (Exclude matching leads)
                          </Box>
                        </MenuItem>
                        <MenuItem value="whitelist">
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircle color="success" />
                            Whitelist (Only allow matching leads)
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Campaign Assignment
                    </Typography>
                    <CampaignSelector
                      accountId={accountId}
                      selectedCampaigns={uploadOptions.targetCampaignIds}
                      onChange={(campaignIds) => setUploadOptions(prev => ({ ...prev, targetCampaignIds: campaignIds }))}
                      helperText="Select campaigns to automatically assign the suppression list to"
                    />
                    
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={uploadOptions.autoAssignCampaigns}
                            onChange={(e) => setUploadOptions(prev => ({ ...prev, autoAssignCampaigns: e.target.checked }))}
                            color="primary"
                          />
                        }
                        label="Auto-assign to campaigns"
                      />
                    </Box>
                    
                    <Box sx={{ mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={uploadOptions.campaignSpecificImport}
                            onChange={(e) => setUploadOptions(prev => ({ ...prev, campaignSpecificImport: e.target.checked }))}
                            color="primary"
                          />
                        }
                        label="Campaign-specific import"
                      />
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Description"
                      value={uploadOptions.description}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Optional description for this import"
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Import Summary
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Account"
                          secondary={accounts.find(acc => acc.id === selectedAccount)?.name || 'Not selected'}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="File"
                          secondary={selectedFile?.name}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Import Type"
                          secondary={importTypes.find(t => t.value === importType)?.label}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {uploadOptions.list_type === 'whitelist' ? <CheckCircle color="success" /> : <BlockIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText
                          primary="List Type"
                          secondary={uploadOptions.list_type === 'whitelist' ? 'Whitelist (Only allow matching leads)' : 'Blacklist (Exclude matching leads)'}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Total Records"
                          secondary={previewData?.total_rows || 'Unknown'}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Batch Size"
                          secondary={`${uploadOptions.batchSize} records per batch`}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Target Campaigns"
                          secondary={uploadOptions.targetCampaignIds.length > 0 ? `${uploadOptions.targetCampaignIds.length} selected` : 'None selected'}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Campaign Assignment"
                          secondary={uploadOptions.autoAssignCampaigns ? 'Auto-assign enabled' : 'Manual assignment required'}
                          primaryTypographyProps={{ color: 'text.primary' }}
                          secondaryTypographyProps={{ color: 'text.secondary' }}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              Upload & Processing
            </Typography>
            
            {currentJob ? (
              <Box>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" color="text.primary" gutterBottom>
                      Current Job: {currentJob.job_id}
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Progress: {jobProgress}%
                      </Typography>
                      <LinearProgress variant="determinate" value={jobProgress} sx={{ height: 8, borderRadius: 4 }} />
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Chip
                          icon={getStatusIcon(jobStatus)}
                          label={jobStatus?.toUpperCase()}
                          color={getStatusColor(jobStatus)}
                        />
                      </Grid>
                      {currentJob.total_records && (
                        <Grid item xs={12} md={3}>
                          <Chip label={`Total: ${currentJob.total_records}`} color="primary" />
                        </Grid>
                      )}
                      {currentJob.processed_records && (
                        <Grid item xs={12} md={3}>
                          <Chip label={`Processed: ${currentJob.processed_records}`} color="info" />
                        </Grid>
                      )}
                      {currentJob.successful_records && (
                        <Grid item xs={12} md={3}>
                          <Chip label={`Success: ${currentJob.successful_records}`} color="success" />
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<History />}
                    onClick={() => setShowJobsDialog(true)}
                  >
                    View All Jobs
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Assessment />}
                    onClick={() => window.location.href = '/lead-gen/jobs'}
                  >
                    Go to Jobs
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  No active job
                </Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" color="text.primary">
          Bulk Upload Suppression Lists
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={() => navigate('/lead-gen/suppression/bulk-jobs')}
          >
            View All Jobs
          </Button>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={() => setShowJobsDialog(true)}
          >
            Recent Jobs
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {renderStepContent(index)}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mr: 1 }}
                      disabled={loading}
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    {index === 1 && (
                      <Button
                        variant="outlined"
                        onClick={handlePreview}
                        disabled={loading || !selectedFile || !importType}
                        startIcon={loading ? <CircularProgress size={20} /> : <Visibility />}
                      >
                        Preview
                      </Button>
                    )}
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Jobs Dialog */}
      <Dialog
        open={showJobsDialog}
        onClose={() => setShowJobsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="text.primary">
            Import Jobs
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell color="text.primary">Job ID</TableCell>
                  <TableCell color="text.primary">Name</TableCell>
                  <TableCell color="text.primary">Type</TableCell>
                  <TableCell color="text.primary">Status</TableCell>
                  <TableCell color="text.primary">Progress</TableCell>
                  <TableCell color="text.primary">Created</TableCell>
                  <TableCell color="text.primary">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell color="text.primary">{job.id}</TableCell>
                    <TableCell color="text.primary">{job.name}</TableCell>
                    <TableCell color="text.primary">{job.import_type}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(job.status)}
                        label={job.status}
                        color={getStatusColor(job.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell color="text.primary">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={job.progress_percentage || 0}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography variant="body2" color="text.secondary">
                            {job.progress_percentage || 0}%
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell color="text.primary">
                      {new Date(job.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {job.status === 'pending' && (
                          <IconButton
                            size="small"
                            onClick={() => handleJobAction(job.id, 'start')}
                            color="primary"
                          >
                            <PlayArrow />
                          </IconButton>
                        )}
                        {job.status === 'processing' && (
                          <IconButton
                            size="small"
                            onClick={() => handleJobAction(job.id, 'cancel')}
                            color="warning"
                          >
                            <Stop />
                          </IconButton>
                        )}
                        {job.status === 'failed' && (
                          <IconButton
                            size="small"
                            onClick={() => handleJobAction(job.id, 'retry')}
                            color="primary"
                          >
                            <Refresh />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowJobsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkUploadSuppression; 